import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export type NavItemActionsProps = {
	/**
	 * Whether the item can be moved down in the list. Defaults to true.
	 * Should be false for the last item, and in other circumstances where moving down is not possible.
	 */
	canMoveDown?: boolean;
	/**
	 * Whether the item can be moved up in the list. Defaults to true.
	 * Should be false for the first item, and in other circumstances where moving up is not possible.
	 */
	canMoveUp?: boolean;
	/**
	 * Whether the item can have its visibility in the menu list toggled. Defaults to true.
	 * Should be false for items that should always stay visible, or always stay hidden away in a "more" menu or similar.
	 */
	canToggleVisibility?: boolean;
	/**
	 * Whether the item is reorderable. Defaults to true.
	 * Should be false for fixed-position items.
	 */
	isReorderable?: boolean;
	/**
	 * Whether the item is currently visible. Defaults to true.
	 * Should be false if the item is currently in the "more" menu, if there is one.
	 */
	isVisible?: boolean;
	/**
	 * Function to handle moving the item down in the list.
	 */
	moveDown: () => void;
	/**
	 * Function to handle moving the item to the bottom of the list.
	 */
	moveToBottom: () => void;
	/**
	 * Function to handle moving the item to the top of the list.
	 */
	moveToTop: () => void;
	/**
	 * Function to handle moving the item up in the list.
	 */
	moveUp: () => void;
	/**
	 * Function to handle toggling the item's visibility in the menu list.
	 */
	toggleVisibility: () => void;
};

export function NavItemActionsMenu({
	isReorderable = true,
	isVisible = true,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	toggleVisibility,
	canMoveUp = true,
	canMoveDown = true,
	canToggleVisibility = true,
}: NavItemActionsProps) {
	const { formatMessage } = useIntl();

	if (!isReorderable && !canToggleVisibility) {
		return null;
	}

	const isOnly = !canMoveUp && !canMoveDown;

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<IconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(messages.actionsMenuTriggerLabel)}
				/>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{isReorderable && (
					<DropdownMenu
						shouldRenderToParent
						placement="right-start"
						trigger={({ triggerRef, ...triggerProps }) => (
							<DropdownItem
								{...triggerProps}
								ref={triggerRef}
								elemBefore={<GrowVerticalIcon label="" />}
								elemAfter={
									<ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />
								}
								isDisabled={isOnly}
							>
								{formatMessage(messages.reorderingMenuTriggerLabel)}
							</DropdownItem>
						)}
					>
						<DropdownItemGroup>
							<DropdownItem isDisabled={!canMoveUp} onClick={moveToTop}>
								{formatMessage(messages.moveToTop)}
							</DropdownItem>
							<DropdownItem isDisabled={!canMoveUp} onClick={moveUp}>
								{formatMessage(messages.moveUp)}
							</DropdownItem>
							<DropdownItem isDisabled={!canMoveDown} onClick={moveDown}>
								{formatMessage(messages.moveDown)}
							</DropdownItem>
							<DropdownItem isDisabled={!canMoveDown} onClick={moveToBottom}>
								{formatMessage(messages.moveToBottom)}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				)}
				{canToggleVisibility && (
					<DropdownItem
						onClick={toggleVisibility}
						elemBefore={
							isVisible ? (
								<EyeOpenStrikethroughIcon
									label=""
									color={token('color.icon')}
									testId="eye-strikethrough-icon"
								/>
							) : (
								<EyeOpenIcon label="" color={token('color.icon')} testId="eye-open-icon" />
							)
						}
					>
						{isVisible ? formatMessage(messages.hideItem) : formatMessage(messages.showItem)}
					</DropdownItem>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
}
